/**
 * Utilitários de rede: fetch com novas tentativas, espera cancelável e pool
 * de concorrência. Sem dependências de DOM (usados também no Web Worker).
 */

export class HttpError extends Error {
  readonly status: number;
  constructor(status: number, url: string) {
    super(`HTTP ${status} em ${url}`);
    this.name = 'HttpError';
    this.status = status;
  }
}

export function isAbortError(e: unknown): boolean {
  return (e as { name?: string })?.name === 'AbortError';
}

export function abortError(): Error {
  const e = new Error('Cancelado');
  e.name = 'AbortError';
  return e;
}

/**
 * Espera a promessa, mas rejeita com AbortError assim que o sinal for
 * acionado (a promessa em si continua; útil para trabalhos compartilhados).
 */
export function abortable<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (v) => {
        signal.removeEventListener('abort', onAbort);
        resolve(v);
      },
      (e: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(e);
      },
    );
  });
}

/** Espera `ms`, rejeitando com AbortError se o sinal for acionado. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError());
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export interface RetryOptions {
  /** Novas tentativas após a primeira (padrão 2). */
  retries?: number;
  /** Espera base entre tentativas, multiplicada pelo número da tentativa (padrão 500 ms). */
  retryDelay?: number;
  signal?: AbortSignal;
  cache?: RequestCache;
  fetchImpl?: typeof fetch;
}

/**
 * GET com novas tentativas para erros de rede, 5xx, 408 e 429. Um 404 devolve
 * null (arquivo ainda não publicado ou já removido pelo INPE).
 */
export async function fetchWithRetry(url: string, opts: RetryOptions = {}): Promise<Response | null> {
  const { retries = 2, retryDelay = 500, signal, cache, fetchImpl = fetch } = opts;
  let lastError: unknown = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (signal?.aborted) throw abortError();
    try {
      const res = await fetchImpl(url, { signal, cache });
      if (res.status === 404) {
        await res.body?.cancel().catch(() => {});
        return null;
      }
      if (res.ok) return res;
      await res.body?.cancel().catch(() => {});
      lastError = new HttpError(res.status, url);
      if (res.status < 500 && res.status !== 408 && res.status !== 429) break;
    } catch (e) {
      if (isAbortError(e) || signal?.aborted) throw abortError();
      lastError = e;
    }
    if (attempt < retries) await sleep(retryDelay * (attempt + 1), signal);
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

/** Lê o corpo como texto, informando o progresso (bytes lidos, total ou 0). */
export async function readTextWithProgress(
  res: Response,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ text: string; bytes: number }> {
  const total = Number(res.headers.get('content-length')) || 0;
  if (!res.body || !onProgress) {
    const buf = new Uint8Array(await res.arrayBuffer());
    onProgress?.(buf.byteLength, total || buf.byteLength);
    return { text: new TextDecoder('utf-8').decode(buf), bytes: buf.byteLength };
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress(loaded, total);
  }
  const all = new Uint8Array(loaded);
  let off = 0;
  for (const c of chunks) {
    all.set(c, off);
    off += c.byteLength;
  }
  return { text: new TextDecoder('utf-8').decode(all), bytes: loaded };
}

/** Executa `task` para cada item com no máximo `limit` em paralelo. */
export async function runPool<T>(
  items: readonly T[],
  limit: number,
  task: (item: T, index: number) => Promise<void>,
  signal?: AbortSignal,
): Promise<void> {
  let next = 0;
  let failed: unknown = null;
  const lane = async () => {
    while (failed === null && next < items.length) {
      if (signal?.aborted) throw abortError();
      const i = next++;
      try {
        await task(items[i]!, i);
      } catch (e) {
        failed ??= e;
        throw e;
      }
    }
  };
  const lanes = Array.from({ length: Math.min(limit, items.length) }, lane);
  const results = await Promise.allSettled(lanes);
  if (failed !== null) throw failed;
  const rejected = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
  if (rejected) throw rejected.reason;
}
